/**
 * Runtime tenant state for the supplier portal — the small, synchronous
 * singleton the UI reads to brand text and gate tenant-optional features.
 *
 * `bootstrapTenantBranding()` fetches the PUBLIC tenant-config once at boot and
 * calls {@link setTenantConfig} BEFORE React renders, so every read below is a
 * cheap synchronous lookup (no context/provider needed for the common case).
 *
 * FAIL-OPEN by contract, mirroring the storefront + native apps: when no tenant
 * is loaded (plain Dumyah, or the best-effort fetch failed) every feature reads
 * `true` and the brand name reads Dumyah's own — so the portal is byte-for-byte
 * unchanged for Dumyah and never blanks a feature it can't confirm.
 */
import {resolveTenant, type TenantBrandingPayload} from './tenantConfig'
import {phoneCountryForValue, phoneValidationMessage, setPhoneValidationConfig} from './phoneValidation'

/**
 * Dumyah's own supplier ("Hisbeh") app store listings — the only tenant for which we ship baked-in
 * download links. Every other tenant must supply its own via the platform config (see
 * {@link tenantAppDownloadLinks}); we never show Dumyah's links under another brand.
 */
const DUMYAH_SUPPLIER_IOS = 'https://apps.apple.com/us/app/dumyah-hisbeh/id6738094504'
const DUMYAH_SUPPLIER_ANDROID = 'https://play.google.com/store/apps/details?id=com.dumyah.partners_app'

let current: TenantBrandingPayload | null = null

/** Record the fetched tenant config (called once at boot, before render). */
export function setTenantConfig(payload: TenantBrandingPayload | null): void {
  current = payload
  setPhoneValidationConfig(payload?.phone_validation)
}

/** Test seam — clear the singleton between tests. */
export function resetTenantConfigForTests(): void {
  setTenantConfig(null)
}

/** The raw payload, or null when none is loaded. */
export function getTenantConfig(): TenantBrandingPayload | null {
  return current
}

/**
 * Why a phone number is invalid for the tenant's Diwan country (or for the country its explicit
 * +/00 prefix names), or '' when it is valid.
 */
export function tenantPhoneValidationMessage(value: unknown, language = 'en', required = true): string {
  return phoneValidationMessage(value, phoneCountryForValue(value, true), language, required)
}

/**
 * The tenant's display name for interpolating into copy. `lang === 'ar'` uses
 * `tenant.name_ar` (falling back to `tenant.name`); otherwise `tenant.name`.
 * Falls back to Dumyah's own name (per language) when no tenant is loaded, so
 * plain-Dumyah copy is unchanged.
 */
export function tenantBrandName(lang: string): string {
  const t = current?.tenant
  // Fallback when no tenant config has loaded: the build's own brand (REACT_APP_BRAND_NAME[_AR], set
  // only by Dumyah's Cloudflare Pages builds), else brand-neutral — the shared fleet build must never
  // bake in a specific tenant's name (that would mislabel every other tenant).
  if (lang === 'ar') {
    return (t?.name_ar || t?.name || process.env.REACT_APP_BRAND_NAME_AR || 'بوابة الموردين') as string
  }
  return (t?.name || process.env.REACT_APP_BRAND_NAME || 'Supplier Portal') as string
}

/**
 * Whether a tenant-optional feature is enabled. FAIL-OPEN: returns `true`
 * unless a tenant is loaded AND has `key` explicitly set to `false`. So a
 * disabled feature (only) hides its UI; Dumyah and absent flags are unchanged.
 */
export function tenantFeatureEnabled(key: string, fallback = true): boolean {
  const features = current?.features
  if (!features || !(key in features)) return fallback
  const v = features[key]
  return v === null || v === undefined ? fallback : Boolean(v)
}

/**
 * Whether the active tenant charges tax. FAIL-OPEN like every other tenant-optional
 * feature: absent ⇒ true (unchanged for Dumyah). When `false`, the UI hides tax
 * inputs/columns (product-form Tax select, GRN invoice tax lines, payment-report tax
 * columns) — the backend correspondingly omits tax fields from its responses.
 */
export const tenantTaxEnabled = () => tenantFeatureEnabled('tax_enabled', true)

// Dumyah's platform tenant code is `default` (what tenant-config returns for dumyah.com); `dumyah` is
// the portal's own local fallback name for it (resolveTenant()).
const DUMYAH_TENANT_CODES = new Set(['default', 'dumyah'])

/**
 * Whether the active tenant is Dumyah itself. The loaded config's `tenant.code` decides; without one
 * (config not loaded / code unreadable) the host decides via resolveTenant() — so Dumyah's own builds
 * (REACT_APP_TENANT=default) and local dev stay unchanged, while a fleet host (`hisbeh.felo.one` ⇒
 * `felo`) never falls back to Dumyah's chrome. Use this to gate Dumyah-specific chrome (its own store
 * links, "Dumyah Ads", tutorial videos) off for white-label tenants.
 */
export function isDefaultTenant(): boolean {
  const code = current?.tenant?.code || resolveTenant()
  return DUMYAH_TENANT_CODES.has(code)
}

/**
 * The code of the currency the store records its amounts in: `locale.base_currency`, else its default
 * currency, else JOD (Dumyah's own, and what every amount was before a store could choose). Felo is ILS.
 */
export function tenantCurrencyCode(): string {
  const locale = current?.locale
  const code = String(locale?.base_currency || locale?.default_currency || '').trim().toUpperCase()
  return code || 'JOD'
}

/** The symbol the store prints for a currency code (₪ for ILS), or '' when its config names none. */
export function tenantCurrencySymbol(code: string): string {
  const wanted = code.trim().toUpperCase()
  const row = (current?.locale?.currencies ?? []).find((c) => String(c?.code ?? '').trim().toUpperCase() === wanted)
  return String(row?.symbol || row?.symbol_left || row?.symbol_right || '').trim()
}

// Flag assets that exist under public/media/flags, keyed by ISO-3166 alpha-2.
const COUNTRY_FLAGS: Record<string, string> = {
  JO: 'jordan', PS: 'palestine', SA: 'saudi-arabia', AE: 'united-arab-emirates',
  QA: 'qatar', KW: 'kuwait', IQ: 'iraq', EG: 'egypt',
}

/** The flag file (under /media/flags) for the Arabic language switcher: the tenant's home country, else Jordan. */
export function tenantArabicFlag(): string {
  const code = String(current?.locale?.default_country?.code ?? '').toUpperCase()
  return `/media/flags/${COUNTRY_FLAGS[code] ?? 'jordan'}.svg`
}

/**
 * The supplier-app store links to show on the Download-App page. Tenant-provided links (from the
 * platform config) always win; otherwise ONLY Dumyah falls back to its baked-in Hisbeh links. A
 * white-label tenant that hasn't published a supplier app yet gets `null` for that store, so the page
 * hides the button instead of pointing suppliers at Dumyah's app.
 */
export function tenantAppDownloadLinks(): {ios: string | null; android: string | null} {
  const dl = current?.download_app
  const cfgIos = (dl?.ios_url ?? '').trim()
  const cfgAndroid = (dl?.android_url ?? '').trim()
  const dflt = isDefaultTenant()
  return {
    ios: cfgIos || (dflt ? DUMYAH_SUPPLIER_IOS : null),
    android: cfgAndroid || (dflt ? DUMYAH_SUPPLIER_ANDROID : null),
  }
}

/**
 * Whether the portal's "how-to" tutorial ("Video Link") buttons should show at all. The videos are
 * Dumyah's own YouTube walkthroughs, so DEFAULT-OFF for white-label tenants: an explicit
 * `tutorials.enabled` wins; otherwise only Dumyah (or an unresolved config) shows them.
 */
export function tenantTutorialsEnabled(): boolean {
  const enabled = current?.tutorials?.enabled
  if (enabled === null || enabled === undefined) return isDefaultTenant()
  return Boolean(enabled)
}

/**
 * The tutorial video URL to link a given section's "Video Link" button to, or `null` to hide it.
 * Rules: tutorials off ⇒ hidden; a tenant's own `tutorials.links[section]` always wins; otherwise
 * ONLY Dumyah falls back to its baked-in walkthrough (`dumyahFallback`). So a white-label tenant that
 * turns tutorials on but hasn't supplied a link for this section just hides that one button — it never
 * points its suppliers at a Dumyah video.
 */
export function tenantTutorialLink(section: string, dumyahFallback: string): string | null {
  if (!tenantTutorialsEnabled()) return null
  const override = (current?.tutorials?.links?.[section] ?? '').trim()
  if (override) return override
  return isDefaultTenant() ? dumyahFallback : null
}
